// Toast notification system for partner portal (inline CSS, no Tailwind).
// Provides ToastProvider context, useToast() hook, and Toast display component.
// Max 3 visible toasts, auto-dismiss 5s, deduplication within 2s window.

import { createContext, useContext, useCallback, useState, useEffect, useRef, type ReactNode } from 'react';
import { colors } from './styles';

/* ── Types ─────────────────────────────────────────── */

type ToastVariant = 'success' | 'error' | 'warning' | 'info';

interface ToastItem {
  id: string;
  variant: ToastVariant;
  message: string;
  createdAt: number;
}

interface ToastContextValue {
  show: (variant: ToastVariant, message: string) => void;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue>({
  show: () => {},
  dismiss: () => {},
});

/* ── Constants ─────────────────────────────────────── */

const MAX_TOASTS = 3;
const AUTO_DISMISS_MS = 5000;
const DEDUP_WINDOW_MS = 2000;

/* ── Variant styles ────────────────────────────────── */

const variantStyles: Record<ToastVariant, { bg: string; border: string; color: string; icon: string }> = {
  success: { bg: colors.successBg, border: colors.successBorder, color: colors.successAlt, icon: '✓' },
  error: { bg: colors.dangerBg, border: colors.dangerBorder, color: colors.dangerAlt, icon: '✗' },
  warning: { bg: colors.warningBg, border: colors.warningBorder, color: colors.warningAlt, icon: '⚠' },
  info: { bg: colors.cyanBg, border: colors.cyanBorder, color: colors.cyan, icon: 'ℹ' },
};

/* ── CSS keyframes (injected once) ─────────────────── */

const STYLE_ID = 'ailys-toast-keyframes';

function ensureKeyframes() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    @keyframes ailys-toast-in {
      from { opacity: 0; transform: translateY(-8px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes ailys-toast-out {
      from { opacity: 1; transform: translateY(0); }
      to { opacity: 0; transform: translateY(-8px); }
    }
  `;
  document.head.appendChild(style);
}

/* ── Provider ──────────────────────────────────────── */

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const recentRef = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    ensureKeyframes();
  }, []);

  // Auto-dismiss
  useEffect(() => {
    if (toasts.length === 0) return;
    const oldest = toasts[0];
    const remaining = AUTO_DISMISS_MS - (Date.now() - oldest.createdAt);
    const timer = setTimeout(() => {
      setToasts(prev => prev.slice(1));
    }, Math.max(remaining, 0));
    return () => clearTimeout(timer);
  }, [toasts]);

  const show = useCallback((variant: ToastVariant, message: string) => {
    const now = Date.now();

    // Deduplication: skip if same message within 2s
    const dedupKey = `${variant}:${message}`;
    const lastShown = recentRef.current.get(dedupKey);
    if (lastShown && now - lastShown < DEDUP_WINDOW_MS) return;
    recentRef.current.set(dedupKey, now);

    // Clean old dedup entries
    for (const [key, ts] of recentRef.current) {
      if (now - ts > DEDUP_WINDOW_MS * 2) recentRef.current.delete(key);
    }

    const item: ToastItem = {
      id: `toast-${now}-${Math.random().toString(36).substring(2, 6)}`,
      variant,
      message,
      createdAt: now,
    };

    setToasts(prev => {
      const next = [...prev, item];
      // Cap at MAX_TOASTS, drop oldest
      return next.length > MAX_TOASTS ? next.slice(next.length - MAX_TOASTS) : next;
    });
  }, []);

  const dismiss = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ show, dismiss }}>
      {children}
      {/* Toast container */}
      {toasts.length > 0 && (
        <div
          style={{
            position: 'fixed',
            top: 16,
            right: 16,
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            pointerEvents: 'none',
            maxWidth: 380,
            width: '100%',
          }}
        >
          {toasts.map(toast => {
            const vs = variantStyles[toast.variant];
            return (
              <div
                key={toast.id}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                  padding: '12px 16px',
                  borderRadius: 10,
                  background: vs.bg,
                  border: `1px solid ${vs.border}`,
                  backdropFilter: 'blur(12px)',
                  animation: 'ailys-toast-in 0.2s ease-out',
                  pointerEvents: 'auto',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                }}
              >
                <span style={{ fontSize: '1rem', lineHeight: 1, color: vs.color, flexShrink: 0, marginTop: 1 }}>
                  {vs.icon}
                </span>
                <span style={{ fontSize: '0.85rem', color: colors.text, lineHeight: 1.4, flex: 1 }}>
                  {toast.message}
                </span>
                <button
                  onClick={() => dismiss(toast.id)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: colors.mutedLight,
                    cursor: 'pointer',
                    fontSize: '1rem',
                    lineHeight: 1,
                    padding: 0,
                    flexShrink: 0,
                  }}
                  aria-label="Dismiss"
                >
                  &times;
                </button>
              </div>
            );
          })}
        </div>
      )}
    </ToastContext.Provider>
  );
}

/* ── Hook ──────────────────────────────────────────── */

export function useToast() {
  return useContext(ToastContext);
}
